/**
 * **An implicit close is not optimistic**, and this is the two-line state machine that says so.
 *
 * Marking a survey reviewed and watching a clip each resolve the item on the SERVER (R15,
 * `intake-survey` R28, `exercise-clip-timeline` R9/R18). Unlike a queue response there is nothing
 * to withdraw, so the card cannot be struck through before the server has agreed: the first build
 * called `onResolved()` the moment the button was pressed, and a refused `POST
 * /survey/assignment/{id}/mark-reviewed` then left the card struck, counted as done, un-undoable —
 * `unmarkHandled` refuses a `resolved` card — and beyond the reach of the next queue read, which
 * still carries the item but cannot reopen a card `reconcileDeck` preserves the mark on. The
 * trainer's screen said a survey had been reviewed that had not.
 *
 * It is the Pulpit's own invariant, read the other way round: *"an implicit close needs no gate —
 * there the row does not leave until a fresh read says so."* This screen keeps its card, so the
 * gate has to be explicit.
 *
 * Pure: no React, no DOM, no query hook, no i18next. `lib/useImplicitResolution` owns the writes.
 */

/** Which cards have a close in flight, which are already closed, and which one was refused. */
export interface ImplicitCloseState {
  /** Card keys whose write is outstanding. A second press on one of them does nothing. */
  readonly sending: ReadonlySet<string>;
  /**
   * Card keys the server has already accepted a close for.
   *
   * ONCE per card, and only a SUCCESS counts as once. `onPlay` fires again on every resume, every
   * seek out of a pause and every loop, so a trainer who watches one rep three times sent three
   * writes — measured at three in the browser — and heard the live region read the same sentence
   * three times. A REFUSED close is deliberately not in here, which is what lets a later play try
   * again.
   */
  readonly closed: ReadonlySet<string>;
  /**
   * The card the server last refused, or `null`.
   *
   * ONE key rather than a set: the desk shows one card, the sentence belongs on the card it is
   * about, and a refusal the trainer has moved away from is not a refusal they are looking at.
   */
  readonly failedKey: string | null;
}

export const NOTHING_CLOSING: ImplicitCloseState = {
  sending: new Set(),
  closed: new Set(),
  failedKey: null,
};

/**
 * Begin a close, or refuse to.
 *
 * `null` means the write must NOT be sent: one is already outstanding for this card, or the server
 * has already accepted one. It is what stops a second press queueing a duplicate, and what stops
 * `onPlay` — which fires again on every resume, seek and loop — posting `seen` more than once.
 */
export function startImplicitClose(
  state: ImplicitCloseState,
  key: string,
): ImplicitCloseState | null {
  if (state.sending.has(key) || state.closed.has(key)) return null;
  return {
    sending: new Set([...state.sending, key]),
    closed: state.closed,
    // The trainer is trying again, so the old refusal stops being what this card says.
    failedKey: state.failedKey === key ? null : state.failedKey,
  };
}

/** The server agreed. The caller resolves the card; nothing here remembers that it did. */
export function implicitCloseSucceeded(state: ImplicitCloseState, key: string): ImplicitCloseState {
  return {
    sending: without(state.sending, key),
    closed: new Set([...state.closed, key]),
    failedKey: state.failedKey,
  };
}

/**
 * The server refused.
 *
 * The card is left exactly as it was — open, uncounted, and pressable again — and the refusal is
 * named so the surface can print it beside the control that was pressed.
 */
export function implicitCloseRefused(state: ImplicitCloseState, key: string): ImplicitCloseState {
  // NOT added to `closed`: a refused close has to be retryable, and the clip's guard used to be
  // taken before the write, which made a failed `seen` unretryable for the rest of the sitting.
  return { sending: without(state.sending, key), closed: state.closed, failedKey: key };
}

/** Is this card's close in the air? The primary says so and will not take a second press. */
export function implicitCloseIsSending(state: ImplicitCloseState, key: string): boolean {
  return state.sending.has(key);
}

/** Did the server refuse this card's close? The card prints the sentence. */
export function implicitCloseWasRefused(state: ImplicitCloseState, key: string): boolean {
  return state.failedKey === key;
}

function without(keys: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(keys);
  next.delete(key);
  return next;
}
